import { useState } from 'react';
import { Button, IconButton, TextInput } from '../../components/ui';
import { clsx } from '../../components/clsx';
import { MAX_FIELD_LABEL, MAX_HEALTH_FIELDS, type HealthField } from '../../lib/dailies';
import type { HealthState } from './useHealth';

/**
 * The settings view. Swaps in place inside the Health card (the gear in the
 * Card `action` slot toggles it) rather than opening a modal — the spec's
 * "set once in a settings/config view" with nothing to dismiss and no extra
 * chrome to fit in a 350x330 card.
 */
export function HealthSettings({
  health,
  dense,
  onClose,
}: {
  health: HealthState;
  dense: boolean;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState('');

  const submit = () => {
    health.addField(draft);
    setDraft('');
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-1.5">
      <p className="shrink-0 text-[11px] leading-tight text-ink-subtle">
        Your checklist. Ticks reset each midnight; these fields do not.
      </p>

      <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-hidden">
        {health.fields.map((field) => (
          <FieldRow
            key={field.id}
            field={field}
            dense={dense}
            onRename={health.renameField}
            onRemove={health.removeField}
          />
        ))}
      </ul>

      <div className="flex shrink-0 items-center gap-1.5">
        <TextInput
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          maxLength={MAX_FIELD_LABEL}
          disabled={!health.canAdd}
          placeholder={health.canAdd ? 'Add a field…' : `Limit ${MAX_HEALTH_FIELDS} fields`}
          aria-label="New health field"
          className="h-7 flex-1 py-0"
        />
        <Button
          variant="accent"
          onClick={submit}
          disabled={!health.canAdd || draft.trim() === ''}
          className="py-1"
        >
          Add
        </Button>
      </div>

      <div className="flex shrink-0 items-center justify-between">
        <Button variant="ghost" onClick={health.restoreDefaults} className="px-1 py-0.5">
          Restore defaults
        </Button>
        <Button variant="ghost" onClick={onClose} className="px-1 py-0.5">
          Done
        </Button>
      </div>
    </div>
  );
}

/**
 * Rename is edited against a local draft and committed on blur/Enter: writing
 * every keystroke straight to storage would delete the row the instant the
 * input went empty (an empty label is not a renderable field).
 */
function FieldRow({
  field,
  dense,
  onRename,
  onRemove,
}: {
  field: HealthField;
  dense: boolean;
  onRename: (id: string, label: string) => void;
  onRemove: (id: string) => void;
}) {
  const [value, setValue] = useState(field.label);

  const commit = () => {
    const clean = value.trim();
    if (clean === '') {
      setValue(field.label); // reverted, not deleted
      return;
    }
    if (clean !== field.label) onRename(field.id, clean);
  };

  return (
    <li className="flex items-center gap-1">
      <TextInput
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setValue(field.label);
        }}
        maxLength={MAX_FIELD_LABEL}
        aria-label={`Rename ${field.label}`}
        className={clsx('min-w-0 flex-1 py-0', dense ? 'h-6 text-[12px]' : 'h-7')}
      />
      <IconButton label={`Remove ${field.label}`} onClick={() => onRemove(field.id)}>
        <span aria-hidden className="text-xs">
          ✕
        </span>
      </IconButton>
    </li>
  );
}
